import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { SuperadminAuthService } from './auth.service';
import { environment } from '../../../../../libs/environment/environment';

const RETRIED_AFTER_401 = new HttpContextToken<boolean>(() => false);

const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/verify-2fa',
  '/auth/resend-2fa',
  '/auth/send-password-reset-link',
  '/auth/reset-password',
  '/auth/refresh'
];

function isPublicAuthRequest(url: string, apiBaseUrl: string): boolean {
  if (!url.startsWith(apiBaseUrl)) {
    return false;
  }

  const normalizedUrl = url.toLowerCase().split('?')[0];
  return PUBLIC_AUTH_ENDPOINTS.some((endpoint) => normalizedUrl.endsWith(endpoint));
}

export const superadminAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(SuperadminAuthService);
  const apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  if (isPublicAuthRequest(req.url, apiBaseUrl)) {
    return next(req);
  }

  const sendAuthorizedRequest = (accessToken: string, retriedAfter401 = false) => {
    if (!accessToken) {
      return next(req.clone({ context: req.context.set(RETRIED_AFTER_401, retriedAfter401) }));
    }

    return next(req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`
      },
      context: req.context.set(RETRIED_AFTER_401, retriedAfter401)
    }));
  };

  return authService.ensureValidAccessToken().pipe(
    switchMap((accessToken) => {
      return sendAuthorizedRequest(accessToken);
    }),
    catchError((error: unknown) => {
      const httpError = error as HttpErrorResponse;
      const alreadyRetried = req.context.get(RETRIED_AFTER_401);

      if (!(httpError instanceof HttpErrorResponse) || httpError.status !== 401 || alreadyRetried) {
        return throwError(() => error);
      }

      console.warn('[Auth] 401 received, attempting refresh token flow.', {
        method: req.method,
        url: req.url,
      });

      return authService.refreshAfterUnauthorized().pipe(
        switchMap((accessToken) => {
          if (!accessToken) {
            return throwError(() => error);
          }

          return sendAuthorizedRequest(accessToken, true);
        }),
        catchError(() => throwError(() => error))
      );
    })
  );
};
